import { Component, EventEmitter, Output, ViewChild, ElementRef, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { PlateRecognizerService, PlateRecognitionResult } from 'src/app/services/plate-recognizer.service';
import { LoaderService } from 'src/app/core/services/loader.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CustomSnackbarComponent } from '../snackbar/snackbar.component';
import { TranslateService } from '@ngx-translate/core';
import { VehiclePhotoService, PhotoConfig } from 'src/app/pages/work-orders-protocols/services/vehicle-photo.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

const CONFIDENCE_THRESHOLD = 0.75;
const SNACKBAR_DURATION = 4000;
const MAX_IMAGE_SIZE = 5 * 1024 * 1024;

interface PlateCandidate {
    plate: string;
    score: number;
}

interface CameraState {
    isVisible: boolean;
    isLoading: boolean;
    error: string | null;
    stream: MediaStream | null;
    hasMultipleCameras: boolean;
    currentCamera: 'user' | 'environment';
}

@Component({
    selector: 'app-plate-scanner',
    templateUrl: './plate-scanner.component.html',
    styleUrls: ['./plate-scanner.component.scss']
})
export class PlateScannerComponent implements OnDestroy {
    @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
    @ViewChild('cameraVideo') cameraVideo?: ElementRef<HTMLVideoElement>;
    @ViewChild('cameraCanvas') cameraCanvas?: ElementRef<HTMLCanvasElement>;

    @Output() plateDetected = new EventEmitter<string>();
    @Output() closeModal = new EventEmitter<void>();

    // Component state
    private readonly destroy$ = new Subject<void>();

    // Camera state
    cameraState: CameraState = {
        isVisible: false,
        isLoading: false,
        error: null,
        stream: null,
        hasMultipleCameras: false,
        currentCamera: 'environment'
    };

    // Plate recognition state
    isScanning = false;
    detectedPlate: string | null = null;
    confidence: number | null = null;
    candidates: PlateCandidate[] = [];

    // File upload state
    selectedFile: File | null = null;
    imagePreview: string | null = null;

    constructor(
        private plateRecognizerService: PlateRecognizerService,
        private loaderService: LoaderService,
        private snackBar: MatSnackBar,
        private translate: TranslateService,
        private vehiclePhotoService: VehiclePhotoService,
        private cdr: ChangeDetectorRef
    ) { }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.cleanup();
    }

    // ==========================================
    // MODAL LIFECYCLE
    // ==========================================

    /**
     * Open the plate scanner modal and initialize camera
     */
    async open(): Promise<void> {
        this.cameraState.isVisible = true;
        this.reset();
        await this.initializeCamera();
    }

    /**
     * Close the modal and cleanup resources
     */
    close(): void {
        this.cameraState.isVisible = false;
        this.cleanup();
        this.closeModal.emit();
    }

    private cleanup(): void {
        this.vehiclePhotoService.stopCamera();
        this.resetCameraState();
    }

    // ==========================================
    // CAMERA MANAGEMENT
    // ==========================================

    private async initializeCamera(): Promise<void> {
        this.cameraState.isLoading = true;
        this.cameraState.error = null;
        this.cdr.detectChanges();

        // Allow browser to render loading state
        await new Promise(resolve => setTimeout(resolve, 50));

        try {
            if (this.cameraVideo && this.cameraCanvas) {
                this.vehiclePhotoService.setVideoElements(this.cameraVideo, this.cameraCanvas);
            }

            const config: PhotoConfig = {
                type: 'additional',
                target: { id: 'plate-scan', name: 'Plate Scan' }
            };

            const success = await this.vehiclePhotoService.startCamera(config);

            if (success) {
                this.updateCameraStateFromService();
            } else {
                this.handleCameraInitializationError();
            }
        } catch (error) {
            console.error('[PlateScanner] Camera initialization error:', error);
            this.setCameraError(this.translate.instant('protocol_orders.cameraError'));
        }
    }

    private updateCameraStateFromService(): void {
        const cameraState = this.vehiclePhotoService.getCameraState();
        this.cameraState.isLoading = cameraState.isLoading;
        this.cameraState.error = cameraState.error;
        this.cameraState.stream = cameraState.stream;
        this.cameraState.hasMultipleCameras = cameraState.hasMultipleCameras;
        this.cameraState.currentCamera = cameraState.facing;
        this.cdr.detectChanges();
    }

    private handleCameraInitializationError(): void {
        const cameraState = this.vehiclePhotoService.getCameraState();

        if (cameraState.error === 'USE_FILE_INPUT_FALLBACK') {
            this.cameraState.error = null;
        } else {
            this.setCameraError(this.translate.instant('protocol_orders.cameraError'));
        }

        this.cameraState.isLoading = false;
    }

    private setCameraError(errorMessage: string): void {
        this.cameraState.error = errorMessage;
        this.cameraState.isLoading = false;
        this.cdr.detectChanges();
    }

    /**
     * Capture photo from camera
     */
    async capturePhoto(): Promise<void> {
        try {
            const photoResult = await this.vehiclePhotoService.capturePhoto();

            if (photoResult.success && photoResult.blob) {
                this.vehiclePhotoService.stopCamera();
                this.cameraState.stream = null;

                this.selectedFile = new File(
                    [photoResult.blob],
                    `plate-${Date.now()}.jpg`,
                    { type: photoResult.mimeType || 'image/jpeg' }
                );

                this.imagePreview = photoResult.base64DataUrl || photoResult.url;
                await this.scanPlate();
            }
        } catch (error) {
            console.error('[PlateScanner] Photo capture error:', error);
            this.showSnackbar(this.translate.instant('plateScanner.scanError'), 'error');
        }
    }

    /**
     * Switch between front and rear cameras
     */
    async switchCamera(): Promise<void> {
        this.cameraState.isLoading = true;
        try {
            await this.vehiclePhotoService.switchCamera();
            this.updateCameraStateFromService();
        } catch (error) {
            console.error('[PlateScanner] Camera switch error:', error);
            this.setCameraError(this.translate.instant('protocol_orders.cameraError'));
        }
    }

    /**
     * Retry camera initialization after error
     */
    async retryCamera(): Promise<void> {
        this.cameraState.error = null;
        this.cameraState.isLoading = true;

        const config: PhotoConfig = {
            type: 'additional',
            target: { id: 'plate-scan', name: 'Plate Scan' }
        };

        try {
            const success = await this.vehiclePhotoService.startCamera(config);

            if (success) {
                this.updateCameraStateFromService();
            } else {
                this.setCameraError(this.translate.instant('protocol_orders.cameraError'));
            }
        } catch (error) {
            console.error('[PlateScanner] Camera retry error:', error);
            this.setCameraError(this.translate.instant('protocol_orders.cameraError'));
        }
    }

    // ==========================================
    // FILE UPLOAD
    // ==========================================

    /**
     * Handle file selection from input
     */
    onFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (!input.files || !input.files[0]) return;

        const file = input.files[0];

        // Validate file size
        if (file.size > MAX_IMAGE_SIZE) {
            this.showSnackbar(
                this.translate.instant('plateScanner.imageTooLarge'),
                'error'
            );
            return;
        }

        // Validate file type
        if (!this.isValidImageType(file.type)) {
            this.showSnackbar(
                this.translate.instant('plateScanner.invalidImageType'),
                'error'
            );
            return;
        }

        this.selectedFile = file;

        // Stop camera if running
        if (this.cameraState.stream) {
            this.vehiclePhotoService.stopCamera();
            this.cameraState.stream = null;
        }

        // Preview image
        const reader = new FileReader();
        reader.onload = (e: ProgressEvent<FileReader>) => {
            this.imagePreview = e.target?.result as string;
            this.cdr.detectChanges();
        };
        reader.readAsDataURL(file);
    }

    private isValidImageType(mimeType: string): boolean {
        return ['image/jpeg', 'image/png', 'image/webp'].includes(mimeType);
    }

    // ==========================================
    // PLATE SCANNING
    // ==========================================

    /**
     * Scan plate from selected image
     */
    async scanPlate(): Promise<void> {
        if (!this.selectedFile) {
            this.showSnackbar(
                this.translate.instant('plateScanner.noImageSelected'),
                'warning'
            );
            return;
        }

        this.isScanning = true;
        this.detectedPlate = null;
        this.confidence = null;
        this.candidates = [];

        this.loaderService.show();
        this.cdr.detectChanges();

        try {
            const result = await this.plateRecognizerService.recognizePlateFromFile(
                this.selectedFile,
                ['es', 'fr', 'gb', 'de', 'it']
            ).toPromise();

            this.handlePlateRecognitionResult(result);
        } catch (error) {
            this.handlePlateRecognitionError(error);
        } finally {
            this.loaderService.hide();
            this.isScanning = false;
            this.cdr.detectChanges();
        }
    }

    private handlePlateRecognitionResult(result: PlateRecognitionResult | null | undefined): void {
        if (result && result.plate) {
            this.detectedPlate = result.plate.toUpperCase();
            this.confidence = result.score;
            this.candidates = (result.candidates || []).slice(0, 3);

            const confidencePercent = (result.score * 100).toFixed(1);
            this.showSnackbar(
                this.translate.instant('plateScanner.plateDetected', {
                    plate: this.detectedPlate
                }),
                'success'
            );
        } else {
            this.showSnackbar(
                this.translate.instant('plateScanner.noPlateFound'),
                'warning'
            );
        }
    }

    private handlePlateRecognitionError(error: any): void {
        console.error('[PlateScanner] Plate recognition error:', error);

        let errorMessage = this.translate.instant('plateScanner.scanError');

        if (error?.status === 403) {
            errorMessage = this.translate.instant('plateScanner.apiTokenError');
        } else if (error?.status === 429) {
            errorMessage = this.translate.instant('plateScanner.rateLimitError');
        } else if (error?.status === 413) {
            errorMessage = this.translate.instant('plateScanner.imageTooLarge');
        }

        this.showSnackbar(errorMessage, 'error');
    }
    usePlate(plate: string): void {
        if (!plate) return;

        this.plateDetected.emit(plate.toUpperCase());
        this.close();
    }


    useCandidate(candidate: PlateCandidate): void {
        this.usePlate(candidate.plate);
    }


    reset(): void {
        this.selectedFile = null;
        this.imagePreview = null;
        this.isScanning = false;
        this.detectedPlate = null;
        this.confidence = null;
        this.candidates = [];
        this.cdr.detectChanges();
    }

    private resetCameraState(): void {
        this.cameraState = {
            isVisible: false,
            isLoading: false,
            error: null,
            stream: null,
            hasMultipleCameras: false,
            currentCamera: 'environment'
        };
    }

    get isCameraAvailable(): boolean {
        return this.cameraState.stream !== null;
    }

    get isImageLoaded(): boolean {
        return this.imagePreview !== null;
    }

    get isPlateDetected(): boolean {
        return this.detectedPlate !== null;
    }

    getConfidencePercentage(): string {
        if (this.confidence === null) return '0';
        return (this.confidence * 100).toFixed(1);
    }

    isConfidenceHighEnough(): boolean {
        return (this.confidence ?? 0) >= CONFIDENCE_THRESHOLD;
    }

    private showSnackbar(message: string, type: 'success' | 'error' | 'warning'): void {
        this.snackBar.openFromComponent(CustomSnackbarComponent, {
            data: { message, type },
            duration: SNACKBAR_DURATION,
            horizontalPosition: 'end',
            verticalPosition: 'top',
            panelClass: [`snackbar-${type}`]
        });
    }
}
